import React from 'react'
import moment from 'moment'
import { pastDueOf, currencyFormat, currencyFormat0 } from './arAgingModel'
import { companyKeyOf } from './arAgingKeys'

// Past-due line items for the statement paper: only actually-overdue AR items
// (age > 0, balance > 0), flattened across a company's properties. We render the
// fields the payload provides — Terms/Due/Paid are omitted (not in the DB source).
const statementRows = (company) =>
  (company.properties || []).flatMap(property => {
    const entries = [
      ...(property.detailEntries || []),
      ...(property.opportunities || []).flatMap(opp => opp.invoices || [])
    ]
    return entries
      .filter(entry => (entry.amount || 0) > 0 && entry.age > 0)
      .map(entry => ({
        num: entry.arNumber || entry.arType || 'AR item',
        property: property.propertyName,
        arDate: entry.arDate,
        daysLate: entry.age,
        balance: entry.amount || 0
      }))
  }).sort((a, b) => b.daysLate - a.daysLate || b.balance - a.balance)

const agingLine = (buckets) =>
  `AGING · 1-30 ${currencyFormat0(buckets.age30)} · 31-60 ${currencyFormat0(buckets.age60)} · 61-90 ${currencyFormat0(buckets.age90)} · 90+ ${currencyFormat0(buckets.age90Plus)}`

// Full-screen "Preview 15th Run" overlay: run scope = every company with past
// due > 0, minus escalated (shown as "held"). Exclusions persist per run (session
// state in the container). Statement paper frames the email + line-item table.
const ArStatementPreview = ({ companies, excludedKeys, index, onIndexChange, onToggleExclude, onClose }) => {
  const runDate = moment().date(15)
  const withPastDue = companies.filter(company => pastDueOf(company.buckets) > 0)
  const held = withPastDue.filter(company => company.escalated)
  const statements = withPastDue.filter(company => !company.escalated).sort((a, b) => pastDueOf(b.buckets) - pastDueOf(a.buckets))
  const included = statements.filter(company => !excludedKeys.has(companyKeyOf(company)))

  const safeIndex = Math.min(Math.max(0, index), Math.max(0, statements.length - 1))
  const current = statements[safeIndex] || null
  const currentExcluded = current && excludedKeys.has(companyKeyOf(current))

  const includedTotal = included.reduce((sum, company) => sum + pastDueOf(company.buckets), 0)
  const rows = current ? statementRows(current) : []
  const contactLine = current ? (current.billingContacts || []).map(contact => contact.email).filter(Boolean).join(', ') || '—' : ''

  return (
    <div className="ar-preview-scrim">
      <div className="ar-preview">
        <div className="ar-preview-rail">
          <div className="ar-preview-rail-head">
            <div className="ar-eyebrow">15TH RUN · {runDate.format('MMMM D, YYYY').toUpperCase()}</div>
            <div className="ar-preview-summary">{included.length} statements · {currencyFormat(includedTotal)}</div>
            <div className="ar-preview-held">{statements.length - included.length} excluded · {held.length} held (escalated)</div>
          </div>
          <div className="ar-preview-list">
            {statements.map((company, position) => {
              const key = companyKeyOf(company)
              const excluded = excludedKeys.has(key)
              return (
                <div
                  key={key}
                  className={`ar-preview-item ${position === safeIndex ? 'selected' : ''} ${excluded ? 'excluded' : ''}`}
                  onClick={() => onIndexChange(position)}
                >
                  <input
                    type="checkbox"
                    className="ar-check"
                    checked={!excluded}
                    onClick={event => event.stopPropagation()}
                    onChange={() => onToggleExclude(company)}
                    aria-label={`Include ${company.companyName}`}
                  />
                  <span className="ar-preview-item-name">{company.companyName}</span>
                  <span className="ar-preview-item-amt">{currencyFormat0(pastDueOf(company.buckets))}</span>
                </div>
              )
            })}
            {statements.length === 0 && <div className="ar-panel-empty-line">Nothing past due to send.</div>}
          </div>
          <button type="button" className="ar-btn ar-btn-primary ar-preview-done" onClick={onClose}>Done reviewing</button>
        </div>

        <div className="ar-preview-pane">
          <div className="ar-preview-toolbar">
            <div className="ar-preview-pager">
              <button type="button" onClick={() => onIndexChange(Math.max(0, safeIndex - 1))} disabled={safeIndex <= 0}>‹</button>
              <span>{statements.length ? safeIndex + 1 : 0} / {statements.length}</span>
              <button type="button" onClick={() => onIndexChange(Math.min(statements.length - 1, safeIndex + 1))} disabled={safeIndex >= statements.length - 1}>›</button>
            </div>
            {current && (
              <button type="button" className={`ar-preview-toggle ${currentExcluded ? 'excluded' : ''}`} onClick={() => onToggleExclude(current)}>
                {currentExcluded ? 'INCLUDE IN RUN' : 'EXCLUDE FROM RUN'}
              </button>
            )}
          </div>

          <div className="ar-preview-scroll">
            {current && (
              <div className="ar-paper" style={{ opacity: currentExcluded ? 0.45 : 1 }}>
                <div className="ar-paper-email">
                  <div className="ar-paper-field"><span>FROM</span><span>Handyman Maintenance, Inc. &lt;billing@hmiaz.com&gt;</span></div>
                  <div className="ar-paper-field"><span>TO</span><span>{contactLine}</span></div>
                  <div className="ar-paper-field"><span>SUBJECT</span><span>Statement of account — {current.companyName} — {runDate.format('MMMM D, YYYY')}</span></div>
                </div>
                <p className="ar-paper-intro">Hi {(current.billingContacts?.[0]?.name || 'there').split(' ')[0]} — your current statement is below. Reply to this email with any questions.</p>

                <div className="ar-paper-letterhead">
                  <div>
                    <div className="ar-paper-wordmark">HMI</div>
                    <div className="ar-eyebrow">STATEMENT OF ACCOUNT</div>
                  </div>
                  <div className="ar-paper-letterhead-right">
                    <div>{runDate.format('MMMM D, YYYY')}</div>
                    <div className="ar-eyebrow">AUTOMATED 15TH RUN</div>
                  </div>
                </div>

                {current.pauseRequested && (
                  <div className="ar-paper-flag">Internal note — a maintenance pause is requested for this account (not shown to customer)</div>
                )}

                <div className="ar-paper-billed">
                  <div>
                    <div className="ar-paper-label">BILLED TO</div>
                    <div className="ar-paper-billed-name">{current.companyName}</div>
                    <div className="ar-paper-billed-line">{contactLine}</div>
                  </div>
                  <div className="ar-paper-total">
                    <div className="ar-paper-label">TOTAL DUE</div>
                    <div className="ar-paper-total-amt">{currencyFormat(pastDueOf(current.buckets))}</div>
                  </div>
                </div>

                <div className="ar-paper-table">
                  <div className="ar-paper-thead">
                    <span>INVOICE</span><span>PROPERTY</span><span className="ar-paper-num">DAYS LATE</span><span className="ar-paper-num">BALANCE</span>
                  </div>
                  {rows.map((row, position) => (
                    <div key={position} className="ar-paper-trow">
                      <span>{row.num}</span>
                      <span>{row.property}</span>
                      <span className="ar-paper-num">{row.daysLate}</span>
                      <span className="ar-paper-num">{currencyFormat(row.balance)}</span>
                    </div>
                  ))}
                  {rows.length === 0 && <div className="ar-paper-trow ar-paper-empty">No past-due line items.</div>}
                </div>

                <div className="ar-paper-aging">{agingLine(current.buckets)}</div>
                <div className="ar-paper-footer">Questions? Reply to this email or call (602) 307-0040. Thank you for your business.</div>
              </div>
            )}
            {!current && <div className="ar-preview-empty">No statements in this run.</div>}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ArStatementPreview
